import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltipContent } from '@/components/ui/chart';
import { DateRangePicker } from '@/components/ui/date-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/main';
import { getTimezoneAbbreviation, prepareDateRangeForQuery } from '@/utils/date-range';
import { trpc } from '@/utils/trpc';
import { useOrganization, useUser } from '@clerk/clerk-react';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronUp, Filter as FilterIcon, Info, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { z } from 'zod';

// Special value to represent the "Other" bucket in selection
const OTHER_BUCKET_VALUE = '__OTHER__';

const dashboardSearchSchema = z.object({
  toNumber: z.string().optional(),
});

export const Route = createFileRoute('/_protected/dashboard')({
  component: HomeDashboard,
  validateSearch: dashboardSearchSchema,
});

function PerformanceByPhoneTable({
  data,
}: {
  data: {
    phoneNumber: string;
    totalCalls: number;
    resolvedCalls: number;
    resolutionRate: number;
  }[];
}) {
  const [expanded, setExpanded] = useState(false);
  const INITIAL_ROWS = 5;
  const displayedData = expanded ? data : data.slice(0, INITIAL_ROWS);

  if (data.length === 0) {
    return (
      <div className="text-center p-4 text-muted-foreground">
        No call data found for the selected period.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Phone Number</TableHead>
            <TableHead className="text-right">Total Calls</TableHead>
            <TableHead className="text-right">Resolved</TableHead>
            <TableHead className="text-right">Resolution Rate</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedData.map((row) => (
            <TableRow key={row.phoneNumber}>
              <TableCell className="font-mono">{row.phoneNumber}</TableCell>
              <TableCell className="text-right">{row.totalCalls}</TableCell>
              <TableCell className="text-right">{row.resolvedCalls}</TableCell>
              <TableCell className="text-right">{row.resolutionRate.toFixed(1)}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.length > INITIAL_ROWS && (
        <div className="flex justify-center">
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? (
              <>
                Show Less <ChevronUp className="ml-2 h-4 w-4" />
              </>
            ) : (
              <>
                Show More ({data.length - INITIAL_ROWS} more){' '}
                <ChevronDown className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

function HomeDashboard() {
  const { orgId } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const navigate = Route.useNavigate();
  const { toNumber } = Route.useSearch();

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 7);
    return { from: fromDate, to: toDate };
  });

  // Local state for pending filter selections (before Apply is clicked)
  const [pendingToNumbers, setPendingToNumbers] = useState<string[]>([]);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  // Parse URL param to array (applied filter state)
  const toNumberArray = useMemo(
    () => (toNumber ? toNumber.split(',').filter(Boolean) : []),
    [toNumber]
  );

  // Sync local state from URL params on mount/change
  useEffect(() => {
    setPendingToNumbers(toNumberArray);
  }, [toNumberArray]);

  const timezoneAbbr = getTimezoneAbbreviation();

  // Format dates for API using shared utility
  // This ensures consistent date range handling across all pages
  const { startDate, endDate } = prepareDateRangeForQuery(dateRange?.from, dateRange?.to);

  // Get available phone numbers (based on total calls across all time, not date-filtered)
  const { data: phoneNumbersData } = trpc.calls.getAvailableToNumbers.useQuery(undefined, {
    enabled: !!orgId,
  });

  // Build filter options from phone numbers + "Other" bucket if applicable
  const filterOptions = useMemo(() => {
    if (!phoneNumbersData) return [];

    const options: { value: string; label: string }[] = phoneNumbersData.phoneNumbers.map(
      (phoneNumber) => ({ value: phoneNumber, label: phoneNumber })
    );

    // Add "Other" bucket if there are low-volume numbers
    if (phoneNumbersData.hasOther) {
      options.push({ value: OTHER_BUCKET_VALUE, label: 'Other' });
    }

    return options;
  }, [phoneNumbersData]);

  // Derive effective filter values from URL params (not local state)
  // This ensures filters only apply after clicking "Apply"
  const { toNumbers: effectiveToNumbers, includeOther } = useMemo(() => {
    if (toNumberArray.length === 0) {
      return { toNumbers: undefined, includeOther: false };
    }

    // Check if all options are selected (treat as "no filter")
    if (toNumberArray.length === filterOptions.length) {
      return { toNumbers: undefined, includeOther: false };
    }

    // Check if "Other" bucket is selected
    const hasOther = toNumberArray.includes(OTHER_BUCKET_VALUE);

    // Get explicit phone numbers (excluding OTHER_BUCKET_VALUE)
    const explicitNumbers = toNumberArray.filter((v) => v !== OTHER_BUCKET_VALUE);

    // If no explicit numbers and only "Other" selected, pass undefined for toNumbers
    // The backend will use includeOther flag to filter
    const toNumbers = explicitNumbers.length > 0 ? explicitNumbers : undefined;

    return { toNumbers, includeOther: hasOther };
  }, [toNumberArray, filterOptions.length]);

  // Handler for phone number selection changes (updates local state only)
  const handleToNumbersChange = (values: string[]) => {
    setPendingToNumbers(values);
  };

  // Apply filter: update URL params and close dropdown
  const handleApplyFilter = () => {
    setFilterDropdownOpen(false);
    navigate({
      search: (prev) => ({
        ...prev,
        toNumber: pendingToNumbers.length > 0 ? pendingToNumbers.join(',') : undefined,
      }),
    });
  };

  // Clear filter: reset both local state and URL params, close dropdown
  const handleClearFilter = () => {
    setPendingToNumbers([]);
    setFilterDropdownOpen(false);
    navigate({
      search: (prev) => ({
        ...prev,
        toNumber: undefined,
      }),
    });
  };

  // Summary stats
  const {
    data: summaryStats,
    isLoading: loadingStats,
    isFetching: fetchingStats,
  } = trpc.calls.getCallSummaryStats.useQuery(
    { startDate, endDate, toNumber: effectiveToNumbers, includeOther },
    { enabled: !!orgId }
  );

  // Call volume over time - pass user's timezone for local date grouping in chart
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const {
    data: callVolumeData,
    isLoading: loadingVolume,
    isFetching: fetchingVolume,
  } = trpc.calls.getCallVolumeOverTime.useQuery(
    { startDate, endDate, timezone: userTimezone, toNumbers: effectiveToNumbers, includeOther },
    { enabled: !!orgId }
  );

  // Topic categories
  const {
    data: topicCountsData,
    isLoading: loadingTopics,
    isFetching: fetchingTopics,
  } = trpc.calls.getTopicCategoryCounts.useQuery(
    { startDate, endDate, toNumbers: effectiveToNumbers, includeOther },
    { enabled: !!orgId }
  );

  // Average call duration
  const {
    data: avgDurationData,
    isLoading: loadingAvgDuration,
    isFetching: fetchingAvgDuration,
  } = trpc.calls.getAverageCallDuration.useQuery(
    { startDate, endDate, toNumbers: effectiveToNumbers, includeOther },
    { enabled: !!orgId }
  );

  const {
    data: performanceByPhone = [],
    isLoading: loadingPerformance,
    isFetching: fetchingPerformance,
  } = trpc.calls.getPerformanceByPhone.useQuery(
    { startDate, endDate, toNumbers: effectiveToNumbers, includeOther },
    { enabled: !!orgId }
  );

  // Combined fetching state for filter loading indicator
  const isFilterFetching =
    fetchingStats || fetchingVolume || fetchingTopics || fetchingAvgDuration || fetchingPerformance;

  // Convert count fields to numbers for charts
  const callVolumeChartData = (callVolumeData || []).map((d) => ({ ...d, count: Number(d.count) }));
  const topicCountsChartData = (topicCountsData || []).map((d) => ({
    ...d,
    count: Number(d.count),
  }));

  // Resolution rate
  const totalCalls = summaryStats?.all || 0;
  const resolvedCalls = summaryStats?.resolved || 0;
  const resolutionRate = totalCalls ? (resolvedCalls / totalCalls) * 100 : 0;

  // Group topic counts for pie chart: top 5 + 'Other'
  const sortedTopics = [...topicCountsChartData].sort((a, b) => b.count - a.count);
  const topTopics = sortedTopics.slice(0, 5);
  const otherCount = sortedTopics.slice(5).reduce((sum, t) => sum + t.count, 0);
  const pieChartData =
    otherCount > 0 ? [...topTopics, { tag: 'Other', count: otherCount }] : topTopics;

  // Table: top 8 topics, sorted descending
  const tableData = sortedTopics.slice(0, 8);

  // Pastel color palette for pie chart slices
  const pieColors = [
    '#60a5fa', // blue-400
    '#fbbf24', // yellow-400
    '#34d399', // green-400
    '#f87171', // red-400
    '#a78bfa', // purple-400
    '#f472b6', // pink-400
    '#38bdf8', // sky-400
    '#facc15', // amber-400
    '#4ade80', // emerald-400
  ];

  return (
    <div className="flex min-h-svh flex-col items-center gap-8 p-4">
      <div className="w-full max-w-5xl flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <h1 className="text-3xl font-bold">
            {orgLoaded && organization ? (
              organization.name
            ) : userLoaded ? (
              'Overview'
            ) : (
              <Loader2 className="animate-spin inline-block h-8 w-8" />
            )}
          </h1>
          <div className="flex flex-col items-end gap-2">
            <span className="text-xs text-muted-foreground">Times shown in {timezoneAbbr}</span>
            <div className="flex items-center gap-2">
              <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />

              <DropdownMenu open={filterDropdownOpen} onOpenChange={setFilterDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8">
                    {isFilterFetching ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FilterIcon className="mr-2 h-4 w-4" />
                    )}
                    Filters
                    {toNumberArray.length > 0 && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({toNumberArray.length})
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-80 p-4 space-y-4" align="end">
                  <DropdownMenuLabel>Filter by</DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Phone Number</Label>
                    <MultiSelectFilter
                      label="Phone Number"
                      options={filterOptions}
                      selectedValues={pendingToNumbers}
                      onSelectionChange={handleToNumbersChange}
                      placeholder="Select numbers..."
                    />
                  </div>

                  <DropdownMenuSeparator />
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearFilter}
                      disabled={isFilterFetching}
                    >
                      Clear
                    </Button>
                    <Button size="sm" onClick={handleApplyFilter} disabled={isFilterFetching}>
                      Apply
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                Total Calls
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 ml-2 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>All calls received by the system within the selected date range.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loadingStats ? <Loader2 className="animate-spin" /> : (summaryStats?.all ?? 0)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                Trapeze Resolved Calls
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 ml-2 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Calls clearly resolved by the agent, including appropriate transfers.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loadingStats ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  (summaryStats?.resolved ?? 0)
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                Trapeze Resolution Rate
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 ml-2 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Percentage of total calls that were resolved by Trapeze.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loadingStats ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  `${resolutionRate.toFixed(1)}%`
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                Avg. Call Duration
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 ml-2 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Average length of calls in seconds within the selected date range.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loadingAvgDuration ? (
                  <Loader2 className="animate-spin" />
                ) : avgDurationData ? (
                  `${Math.round(avgDurationData.avgDurationMs / 1000)}s`
                ) : (
                  '0s'
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Performance by Phone Number Table */}
        <Card className="col-span-full">
          <CardHeader>
            <CardTitle>Performance by Phone Number</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingPerformance ? (
              <div className="flex justify-center p-4">
                <Loader2 className="animate-spin h-6 w-6" />
              </div>
            ) : (
              <PerformanceByPhoneTable data={performanceByPhone} />
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                Call Volume Over Time
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 ml-2 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Number of calls received per day/period within the selected date range.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{ count: { label: 'Calls', color: 'hsl(var(--chart-1))' } }}
                className="min-h-[220px] w-full"
              >
                <div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={callVolumeChartData}
                      margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  {loadingVolume && <Loader2 className="animate-spin mx-auto mt-8" />}
                </div>
              </ChartContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                Topic Categories
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 ml-2 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Breakdown of calls by the main topics discussed.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{ count: { label: 'Calls', color: 'hsl(var(--chart-2))' } }}
                className="min-h-[220px] w-full"
              >
                <div className="flex items-center justify-center w-full">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        dataKey="count"
                        nameKey="tag"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#60a5fa"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieChartData.map((entry) => (
                          <Cell
                            key={`cell-${entry.tag}`}
                            fill={pieColors[pieChartData.indexOf(entry) % pieColors.length]}
                          />
                        ))}
                      </Pie>
                      <Legend />
                      <RechartsTooltip content={<ChartTooltipContent nameKey="tag" />} />
                    </PieChart>
                  </ResponsiveContainer>
                  {loadingTopics && <Loader2 className="animate-spin mx-auto mt-8" />}
                </div>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
        {/* Topic Counts Data Table in its own card */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                Topic Counts (Top 8)
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 ml-2 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Table showing the most frequent call topics and their counts.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-56">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Topic</TableHead>
                      <TableHead>Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableData.map((row) => (
                      <TableRow key={row.tag}>
                        <TableCell className="whitespace-nowrap px-2 py-1">{row.tag}</TableCell>
                        <TableCell className="px-2 py-1 text-right">{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
